/**
 * SOPJobWorkspace - Post-creation workspace for a SOP job
 *
 * Shows: created job number, 4 artwork upload categories (chunked upload),
 * uploaded file lists with delete, and Master Folder ZIP download.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  CheckCircle, Upload, Trash2, Download, FolderArchive, Tags,
  FileImage, Printer, Layers, FolderOpen, Loader2, AlertTriangle, Plus, Pencil, Check, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CHUNK_SIZE = 4 * 1024 * 1024;

const CATEGORIES = [
  { id: 'small_format', label: 'Small Format', icon: FileImage, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  { id: 'large_format', label: 'Large Format', icon: Layers, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  { id: 'litho', label: 'Litho', icon: Printer, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  { id: 'po_other', label: 'PO & Other', icon: FolderOpen, color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200' }
];

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SOPJobWorkspace = ({ job, masterFolderName, customerMatched, pin, kioskKey, onCreateAnother }) => {
  const [files, setFiles] = useState({});
  const [totalFiles, setTotalFiles] = useState(0);
  const [uploading, setUploading] = useState({});
  const [progress, setProgress] = useState({});
  const [error, setError] = useState('');
  const [boxes, setBoxes] = useState(4);
  const [numbering, setNumbering] = useState(true);
  const [folderName, setFolderName] = useState(masterFolderName);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const inputRefs = useRef({});

  const startEditName = () => { setNameDraft(folderName); setEditingName(true); };

  const saveFolderName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) return;
    if (trimmed === folderName) { setEditingName(false); return; }
    setSavingName(true);
    setError('');
    try {
      const res = await axios.patch(`${API}/sop/jobs/${job.jobNumber}/master-folder`, { pin, name: trimmed });
      setFolderName(res.data.masterFolderName);
      setEditingName(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to rename the master folder.');
    } finally {
      setSavingName(false);
    }
  };

  const authQuery = kioskKey
    ? `key=${encodeURIComponent(kioskKey)}`
    : `pin=${encodeURIComponent(pin)}`;

  const loadFiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sop/jobs/${job.jobNumber}/files?${authQuery}`);
      setFiles(res.data.files || {});
      setTotalFiles(res.data.totalFiles || 0);
    } catch (err) {
      // silent - list refreshes on next action
    }
  }, [job.jobNumber, authQuery]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const uploadFiles = async (category, fileList) => {
    setError('');
    setUploading(prev => ({ ...prev, [category]: true }));
    try {
      for (const file of Array.from(fileList)) {
        const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        for (let i = 0; i < totalChunks; i++) {
          const fd = new FormData();
          fd.append('pin', pin);
          fd.append('kiosk_key', kioskKey);
          fd.append('category', category);
          fd.append('filename', file.name);
          fd.append('chunk_index', i);
          fd.append('total_chunks', totalChunks);
          fd.append('file', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
          await axios.post(`${API}/sop/jobs/${job.jobNumber}/upload-chunk`, fd);
          setProgress(prev => ({ ...prev, [category]: { name: file.name, pct: Math.round(((i + 1) / totalChunks) * 100) } }));
        }
      }
      await loadFiles();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [category]: false }));
      setProgress(prev => ({ ...prev, [category]: null }));
    }
  };

  const deleteFile = async (category, filename) => {
    setError('');
    try {
      await axios.delete(`${API}/sop/jobs/${job.jobNumber}/files/${category}/${encodeURIComponent(filename)}?${authQuery}`);
      await loadFiles();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete file.');
    }
  };

  const downloadUrl = `${API}/sop/jobs/${job.jobNumber}/download?${authQuery}`;

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Success Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 text-center" data-testid="sop-success-screen">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800" data-testid="sop-success-message">Job Created Successfully!</h1>
          <p className="text-4xl font-black text-emerald-700 mt-2" data-testid="sop-job-number">#{job.jobNumber}</p>
          {customerMatched !== undefined && (
            <p className="text-sm mt-1 text-emerald-600" data-testid="sop-customer-match-note">
              Linked to existing PrintLogic customer
            </p>
          )}
          {job.url && (
            <a href={job.url} target="_blank" rel="noopener noreferrer" data-testid="sop-job-link"
               className="text-emerald-600 underline text-sm mt-1 inline-block">
              Open in Print Logic
            </a>
          )}
          <div className="bg-gray-50 rounded-xl px-4 py-3 mt-4 inline-block w-full max-w-xl">
            <p className="text-gray-500 text-xs">Master Folder</p>
            {editingName ? (
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="text"
                  value={nameDraft}
                  autoFocus
                  maxLength={120}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveFolderName();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  data-testid="sop-master-folder-input"
                  className="flex-1 font-mono font-bold text-gray-800 text-sm px-3 py-2 border-2 border-emerald-400 rounded-lg focus:border-emerald-600 focus:outline-none bg-white"
                />
                <button
                  onClick={saveFolderName}
                  disabled={savingName || !nameDraft.trim()}
                  aria-label="Save folder name"
                  data-testid="sop-master-folder-save-btn"
                  className="p-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-300 transition-colors"
                >
                  {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setEditingName(false)}
                  disabled={savingName}
                  aria-label="Cancel rename"
                  data-testid="sop-master-folder-cancel-btn"
                  className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <p className="font-mono font-bold text-gray-800 text-sm break-all" data-testid="sop-master-folder-name">{folderName}</p>
                <button
                  onClick={startEditName}
                  aria-label="Edit folder name"
                  data-testid="sop-master-folder-edit-btn"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors flex-shrink-0"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Upload Section */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-3">Upload Artwork / PDFs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const catFiles = files[cat.id] || [];
              const isUploading = uploading[cat.id];
              const prog = progress[cat.id];
              return (
                <div key={cat.id} className={`bg-white rounded-2xl shadow p-5 border-2 ${cat.border}`} data-testid={`sop-upload-card-${cat.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 ${cat.bg} rounded-xl flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${cat.color}`} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">{cat.label}</p>
                      <p className="text-xs text-gray-400">{catFiles.length} file{catFiles.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {catFiles.length > 0 && (
                    <ul className="mb-3 space-y-1">
                      {catFiles.map(f => (
                        <li key={f.name} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm" data-testid={`sop-file-${cat.id}-${f.name}`}>
                          <span className="text-gray-700 truncate mr-2">{f.name}</span>
                          <span className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-gray-400 text-xs">{formatSize(f.size)}</span>
                            <button
                              onClick={() => deleteFile(cat.id, f.name)}
                              aria-label={`Delete ${f.name}`}
                              data-testid={`sop-delete-${cat.id}-${f.name}`}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {isUploading && prog && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 truncate mb-1">{prog.name} — {prog.pct}%</p>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${prog.pct}%` }} />
                      </div>
                    </div>
                  )}

                  <input
                    type="file"
                    multiple
                    className="hidden"
                    ref={el => { inputRefs.current[cat.id] = el; }}
                    onChange={(e) => {
                      if (e.target.files?.length) uploadFiles(cat.id, e.target.files);
                      e.target.value = '';
                    }}
                    data-testid={`sop-file-input-${cat.id}`}
                  />
                  <button
                    onClick={() => inputRefs.current[cat.id]?.click()}
                    disabled={isUploading}
                    data-testid={`sop-upload-btn-${cat.id}`}
                    className={`w-full py-2.5 rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2 ${
                      isUploading ? 'bg-gray-100 text-gray-400' : `${cat.bg} ${cat.color} hover:opacity-80`
                    }`}
                  >
                    {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {isUploading ? 'Uploading...' : 'Upload Files'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl mb-6" data-testid="sop-workspace-error">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Download Master Folder */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <FolderArchive className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-bold text-gray-800">Download Master Folder</p>
                <p className="text-sm text-gray-500">
                  {totalFiles > 0
                    ? `${totalFiles} file${totalFiles !== 1 ? 's' : ''} ready — ZIP named "${folderName}"`
                    : 'Upload files above to enable the download'}
                </p>
              </div>
            </div>
            <a
              href={totalFiles > 0 ? downloadUrl : undefined}
              data-testid="sop-download-master-btn"
              aria-disabled={totalFiles === 0}
              className={`w-full sm:w-auto px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors ${
                totalFiles > 0
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed pointer-events-none'
              }`}
            >
              <Download className="w-5 h-5" />
              Download
            </a>
          </div>
        </div>

        {/* Box Labels */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <Tags className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-800">Box Labels</p>
                <p className="text-sm text-gray-500">A4 PDF — 4 × A6 labels per sheet{numbering ? `, numbered "Box 1 of ${boxes || 1}"` : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="sop-labels-numbering-toggle">
                <input
                  type="checkbox"
                  checked={numbering}
                  onChange={(e) => setNumbering(e.target.checked)}
                  data-testid="sop-labels-numbering-checkbox"
                  className="w-4 h-4 accent-emerald-600"
                />
                <span className="text-sm text-gray-600 whitespace-nowrap">Number cartons</span>
              </label>
              <div className="flex items-center gap-2">
                <label htmlFor="sop-box-count" className="text-sm font-medium text-gray-600 whitespace-nowrap">Boxes:</label>
                <input
                  id="sop-box-count"
                  type="number"
                  min="1"
                  max="500"
                  value={boxes}
                  onChange={(e) => setBoxes(e.target.value === '' ? '' : Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                  data-testid="sop-box-count-input"
                  className="w-20 px-3 py-2.5 border-2 border-gray-200 rounded-xl text-gray-900 text-center font-bold focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <a
                href={`${API}/sop/jobs/${job.jobNumber}/box-labels?${authQuery}&boxes=${boxes || 1}&numbering=${numbering}`}
                data-testid="sop-download-labels-btn"
                className="flex-1 sm:flex-none px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-700 text-white transition-colors"
              >
                <Download className="w-5 h-5" />
                Labels
              </a>
            </div>
          </div>
        </div>

        {/* Create Another */}
        <button
          onClick={onCreateAnother}
          data-testid="sop-create-another-btn"
          className="w-full border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Another Job
        </button>
      </div>
    </div>
  );
};

export default SOPJobWorkspace;
